import type { ReactNode } from "react";

export interface HighlightTextOptions {
    /** CSS class applied to highlighted spans. If omitted, highlighted parts render as plain spans. */
    highlightClassName?: string;
}

/**
 * Highlights snippets in text by wrapping matching portions in spans.
 * Uses the highlightSnippets array from GAIA response to mark portions of text.
 *
 * Example from GAIA:
 *   rewritten_text: "Your phone is now fixed. Please let me know..."
 *   highlight_snippets: ["AWESOME!!!", "ur", "kno"]
 *
 * Sorts snippets by length (longest first) to avoid partial matches.
 * Escapes regex special characters in snippets for safe pattern matching.
 *
 * @param text - The text to highlight
 * @param highlightSnippets - Array of substring patterns to highlight (case-insensitive)
 * @param options - Optional config, e.g. highlightClassName for styling
 * @returns ReactNode - text with matching portions wrapped in spans
 */
export function highlightText(text: string, highlightSnippets: string[], options?: HighlightTextOptions): ReactNode {
    if (!highlightSnippets || highlightSnippets.length === 0) {
        return text;
    }

    const { highlightClassName } = options ?? {};

    // Sort snippets by length (longest first) to avoid partial matches
    const sortedSnippets = [...highlightSnippets].sort((a, b) => b.length - a.length);

    // Create a regex pattern that matches any of the snippets
    const escapedSnippets = sortedSnippets.map((snippet) => snippet.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
    const pattern = new RegExp(`(${escapedSnippets.join("|")})`, "gi");

    // Split text by the pattern and wrap matches
    const parts = text.split(pattern);

    return parts.map((part, index) => {
        const isHighlighted = sortedSnippets.some((snippet) => snippet.toLowerCase() === part.toLowerCase());

        if (isHighlighted) {
            return (
                <span key={index} className={highlightClassName}>
                    {part}
                </span>
            );
        }
        return part;
    });
}
// TODO: get this working well in the regular input box too
